// The pieces a section's extra rows are built from (take-off-extras.tsx,
// take-off-hire.tsx): one row with its inputs and figures, an "+ Add …" row
// and a pick-to-add row, and the job-price tag.
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import type { OptionScope } from '@higgins/m2q-pricing';
import { AddAction, AddActionLabel } from './add-action';
import { RemoveButton } from './remove-button';
import { COLUMN_COUNT, ExtraFigures, PINNED_ROW } from './take-off-rows';

/** Columns before Hours, where an extra row's label and inputs go. */
const LABEL_SPAN = 7;

export type Edit = (change: (scope: OptionScope) => OptionScope) => void | Promise<void>;

/** A removable row: what to ask, and the removal once confirmed. */
interface Removal {
  question: string;
  remove: () => void;
}

/** One extra row: what it is and its inputs across the surface columns, then hours, litres and sell. */
export function Row({ children, hours, litres, sell, removal }: { children: ReactNode; hours?: number; litres?: number; sell?: number; removal?: Removal }) {
  return (
    <tr className="text-slate-700">
      <td colSpan={LABEL_SPAN} className="px-2 py-1">
        <div className={`${PINNED_ROW} flex w-fit flex-wrap items-center gap-1.5 text-sm`}>
          {removal && (
            <RemoveButton question={removal.question} onRemove={removal.remove} label="Remove this line" title="Remove" className="text-slate-400 hover:text-red-700">
              <X className="h-3.5 w-3.5" />
            </RemoveButton>
          )}
          {children}
        </div>
      </td>
      <ExtraFigures hours={hours} litres={litres} sell={sell} />
    </tr>
  );
}

/** An "+ Add …" row under a section's lines. */
export function AddLine({ label, onAdd }: { label: string; onAdd: () => void }) {
  return (
    <tr>
      <td colSpan={COLUMN_COUNT} className="px-2 py-1">
        <AddAction onClick={onAdd} className={PINNED_ROW}>
          {label}
        </AddAction>
      </td>
    </tr>
  );
}

/** An "+ Add …" row with a list to pick from: `onPick` gets the chosen option's value. */
export function AddFromList({ label, children, onPick }: { label: string; children: ReactNode; onPick: (value: string) => void }) {
  return (
    <tr>
      <td colSpan={COLUMN_COUNT} className="px-2 py-1">
        <label className={`${PINNED_ROW} flex w-fit items-center gap-2`}>
          <AddActionLabel>{label}</AddActionLabel>
          <select value="" onChange={(e) => e.target.value && onPick(e.target.value)} aria-label={label} className="h-7 rounded-md border border-slate-300 bg-white px-1 text-sm text-slate-700">
            <option value="">Choose…</option>
            {children}
          </select>
        </label>
      </td>
    </tr>
  );
}

/** Marks a line priced from the job's prices (#413), for the reviewer. */
export const JobPriceTag = () => <span className="rounded-full bg-amber-100 px-1.5 text-[10px] text-amber-900">job price</span>;
